<template>
  <div
    :class="[
      $options.name,
      isSelected ? $options.name + '--active' : '',
      !(item.products?.length || item.emailEventData) ? $options.name + '--disabled' : '',
      'tw:flex tw:gap-2 tw:p-2',
    ]"
    @click="$emit('click')"
  >
    <div :class="$options.name + '__icon'">
      <font-awesome-icon :icon="getIconType(item.type)" :style="getIconColor(item.type)" />
    </div>

    <div style="flex: 1">
      <p :class="['tw:body-1', item.type === 'INBOX_MESSAGE' ? 'tw:mb-2' : 'tw:mb-0']">
        {{ trans._inbox.customer.activity.filters[item.type] }}
      </p>

      <EdroneBadge
        v-if="item.type !== 'INBOX_MESSAGE'"
        :label="getHighlightedDetails(item)"
        class="tw:mb-2"
        size="small"
        type="default"
      />

      <p
        v-if="item.type !== 'PRODUCT_VIEW' && item.type !== 'ADD_TO_CART' && item.type !== 'INBOX_MESSAGE'"
        :class="[
          $options.name + '__details',
          'tw:caption tw:mb-2',
          item.type.includes('EMAIL') || item.type.includes('AUTOMATION')
            ? $options.name + '__details--capitalize'
            : '',
        ]"
        style="color: #545454"
        v-html="getActivityDetails(item)"
      />

      <p
        v-if="item.type === 'INBOX_MESSAGE'"
        :class="[$options.name + '__details', 'tw:caption tw:mb-2']"
        style="color: #545454"
        v-text="getActivityDetails(item)"
      />

      <p class="tw:caption" style="color: #545454">{{ formatDate(item.date) }}</p>
    </div>

    <div v-if="item.type === 'INBOX_MESSAGE' && item.inboxMessageData" style="margin-left: auto">
      <EdroneBadge
        :label="item.inboxMessageData.inboxConversationMessageType?.split('_')[0]"
        :type="getColor(item.inboxMessageData.inboxConversationMessageType)"
        size="small"
      />
    </div>

    <div v-if="isEdroneImpacted(item)">
      <EdroneBadge
        :tooltip="
          item.orderData?.engagementAttributionReasons
            ? item.orderData?.engagementAttributionReasons.join(' - ').replaceAll('_', ' ').toLowerCase()
            : 'edrone impacted'
        "
        icon="fa-solid fa-bars"
        size="small"
        type="warning"
      />
    </div>

    <div v-if="item.products && item.products.length > 0" :class="$options.name + '__product-image'">
      <img
        v-if="item.products.length === 1 && item.products[0].image"
        :src="item.products[0].image"
        alt="Product image"
        style="max-width: 100%; max-height: 100%; object-fit: cover"
      />
      <EdroneBadge v-else :label="item.products.length.toString()" size="small" />
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from "vue";
import dayjs from "dayjs";
import { Activity, ActivityFiltersType } from "vue-app/pages/machines/machineCustomerPreview";
import EdroneBadge from "edrone-components/EdroneBadge/EdroneBadge.vue";

export default defineComponent({
  name: "CustomerPreviewActivityItem",
  components: { EdroneBadge },
  props: {
    item: {
      type: Object as PropType<Activity>,
      required: true,
    },
    isSelected: {
      type: Boolean,
      required: true,
    },
  },
  emits: ["click"],
  setup() {
    const isDev = window.EDRONE_CONTEXT.user.isDev;

    const formatDate = (date: number) => {
      return dayjs.tz(date, window.EDRONE_CONTEXT.timezone).format("DD MMM HH:mm:ss");
    };

    const getProductName = (item: Activity) => {
      if (!item.products || item.products.length === 0) {
        return "-";
      }
      return item.type === "ORDER" && item.products.length > 1
        ? item.products.length + " products"
        : item.products[0].title || "";
    };

    const getHighlightedDetails = (item: Activity) => {
      let details = "";
      switch (item.type) {
        case "PRODUCT_VIEW":
        case "ADD_TO_CART":
        case "ORDER":
          details = getProductName(item) || "-";
          break;
        case "EMAIL_OPEN":
        case "EMAIL_CLICK":
          details = item.emailEventData?.subject || "-";
          break;
        case "SMS_CLICK":
        case "SMS_DELIVERED":
          details = item.smsEventData?.campaignName || "-";
          break;
        case "WHATSAPP_CLICK":
        case "WHATSAPP_OPEN":
        case "WHATSAPP_DELIVERED":
          details = item.whatsappEventData?.campaignName || "-";
          break;
        case "EMAIL_AUTOMATION_RECEIVED":
          details = item.automationReceivedData?.subject || "-";
          break;
        case "SUBSCRIPTION_CHANGE":
          details =
            item.subscriptionStatusChangeData?.subscriptionStatusBefore +
            " → " +
            item.subscriptionStatusChangeData?.subscriptionStatusAfter;
          break;
        default:
          details = "-";
          break;
      }
      return details?.length > 50 ? details.slice(0, 47) + "..." : details;
    };

    const getActivityDetails = (item: Activity) => {
      switch (item.type) {
        case "ORDER":
          return (
            "Order value: " +
            item.orderData?.basePaymentValue +
            item.orderData?.orderCurrency +
            "\nOrder id: " +
            item.orderData?.orderId +
            (isDev ? "\nCustomer order id: " + item.orderData?.customerOrderId : "")
          );
        case "EMAIL_OPEN":
        case "EMAIL_CLICK":
          return (
            window._trans._readyFlows.automation.name[item.emailEventData?.engagementType || "UNKNOWN"] +
            (isDev ? "\nEngagement message id: " + item.emailEventData?.engagementMessageId : "")
          );
        case "WHATSAPP_OPEN":
        case "WHATSAPP_CLICK":
        case "WHATSAPP_DELIVERED":
          return (
            window._trans._readyFlows.automation.name[item.whatsappEventData?.engagementType || "UNKNOWN"] +
            (isDev ? "\nEngagement message id: " + item.whatsappEventData?.engagementMessageId : "")
          );
        case "SMS_CLICK":
        case "SMS_DELIVERED":
          return (
            window._trans._readyFlows.automation.name[item.smsEventData?.engagementType || "UNKNOWN"] +
            (isDev ? "\nEngagement message id: " + item.smsEventData?.engagementMessageId : "")
          );
        case "EMAIL_AUTOMATION_RECEIVED":
          return (
            (isDev ? getAutomationReceivedStatus(item.automationReceivedData) + "\n" : "") + // STATUS
            //  ENGAGEMENT TYPE
            window._trans._readyFlows.automation.name[item.automationReceivedData?.engagementType || "UNKNOWN"] +
            //  CUSTOM FLOW
            (item.automationReceivedData?.customFlowId
              ? "\nCustom flow name: " +
                item.automationReceivedData?.customFlowName +
                (isDev
                  ? "\nCustom flow id: " +
                    item.automationReceivedData.customFlowId +
                    "\nCustom flow element id: " +
                    item.automationReceivedData.customFlowElementId
                  : "")
              : "") +
            //  EMAIL
            (isDev ? "\nEngagement message id: " + item.automationReceivedData?.engagementMessageId : "") +
            //  OTHER
            (isDev
              ? "\n" +
                (item.automationReceivedData?.engagementId
                  ? "Engagement id: " + item.automationReceivedData.engagementId
                  : "") +
                (item.automationReceivedData?.customerEngagementId
                  ? "\nCustomer engagement id: " + item.automationReceivedData.customerEngagementId
                  : "")
              : "")
          );
        case "SUBSCRIPTION_CHANGE":
          return isDev
            ? "Reason: " + item.subscriptionStatusChangeData?.subscriptionStatusReasonAfter?.replaceAll("_", " ")
            : "";
        case "INBOX_MESSAGE":
          return item.inboxMessageData?.message
            ? item.inboxMessageData?.message.length > 120
              ? item.inboxMessageData?.message.slice(0, 117) + "..."
              : item.inboxMessageData?.message
            : "-";
        default:
          return "-";
      }
    };

    const getAutomationReceivedStatus = (
      details: null | {
        customerEngagementStatus: string | null;
        notDisplayedReasons: object | null;
        receivedEmail: boolean;
        requestLocalRejectReason: string | null;
      }
    ) => {
      return (
        (details?.receivedEmail ? "✅ Email received" : "❌ Email not received") +
        "\nStatus: " +
        details?.customerEngagementStatus?.replaceAll("_", " ") +
        (details?.requestLocalRejectReason
          ? " (" + details?.requestLocalRejectReason.replaceAll("_", " ").toLowerCase() + ")"
          : "") +
        (details?.notDisplayedReasons ? "\nNot displayed reasons:\n" + JSON.stringify(details.notDisplayedReasons) : "")
      );
    };

    const getIconType = (type: ActivityFiltersType) => {
      switch (type) {
        case "PRODUCT_VIEW":
          return "fa-light fa-eye";
        case "ADD_TO_CART":
          return "fa-light fa-cart-arrow-down";
        case "ORDER":
          return "fa-light fa-sack-dollar";
        case "INBOX_MESSAGE":
          return "fa-light fa-messages";
        case "SUBSCRIPTION_CHANGE":
          return "fa-light fa-user";
        case "EMAIL_AUTOMATION_RECEIVED":
          return "fa-light fa-envelope-dot";
        case "EMAIL_OPEN":
          return "fa-light fa-envelope-open";
        case "EMAIL_CLICK":
          return "fa-light fa-bullseye-pointer";
        case "WHATSAPP_OPEN":
        case "WHATSAPP_CLICK":
        case "WHATSAPP_DELIVERED":
          return "fa-brands fa-whatsapp";
        case "SMS_DELIVERED":
        case "SMS_CLICK":
          return "fa-light fa-message-sms";
        default:
          return "fa-light fa-arrow-right";
      }
    };

    const getIconColor = (type: ActivityFiltersType) => {
      switch (type) {
        case "ORDER":
          return { color: "#00d756" };
        case "INBOX_MESSAGE":
          return { color: "#1a7dff" };
        case "SUBSCRIPTION_CHANGE":
          return { color: "#ffba08" };
        case "EMAIL_AUTOMATION_RECEIVED":
        case "EMAIL_OPEN":
        case "EMAIL_CLICK":
          return { color: "#542cfa" };
        case "WHATSAPP_CLICK":
        case "WHATSAPP_OPEN":
        case "WHATSAPP_DELIVERED":
          return { color: "#5be48e" };
        case "SMS_CLICK":
        case "SMS_DELIVERED":
          return { color: "#fff482" };
        case "PRODUCT_VIEW":
        case "ADD_TO_CART":
        default:
          return { color: "#050505" };
      }
    };

    const getColor = (type: string | null) => {
      switch (type) {
        case "CHAT":
          return "info";
        case "FACEBOOK":
          return "violet";
        case "WHATSAPP_VONAGE":
        case "WHATSAPP":
          return "success";
        case "EMAIL":
        default:
          return "default";
      }
    };

    const isEdroneImpacted = (item: Activity) => {
      return item.type === "ORDER" && item.orderData?.edroneImpacted;
    };

    return {
      getProductName,
      getHighlightedDetails,
      getActivityDetails,
      formatDate,
      getIconType,
      getIconColor,
      getColor,
      isEdroneImpacted,
    };
  },
});
</script>

<style scoped>
@reference "vue-app/assets/scss/main.css";

.CustomerPreviewActivityItem {
  cursor: pointer;
  border-radius: 4px;
  transition: all 0.2s ease-in-out;
  @apply tw:bg-white tw:border tw:border-default-200;
}

.CustomerPreviewActivityItem:hover {
  @apply tw:border-blue-200;
}

.CustomerPreviewActivityItem--active {
  @apply tw:border-blue-200;
}

.CustomerPreviewActivityItem--disabled {
  pointer-events: none !important;
  @apply tw:bg-default-000 tw:border-default-000;
}

.CustomerPreviewActivityItem__icon {
  font-size: 20px;
  width: 20px;
  height: 20px;
  display: flex;
  justify-content: center;
  align-items: center;
}

.CustomerPreviewActivityItem__product-image {
  border-radius: 4px;
  width: 72px;
  height: 72px;
  margin-left: auto;
  display: flex;
  justify-content: center;
  align-items: center;
  @apply tw:border tw:border-default-200;
}

.CustomerPreviewActivityItem__details {
  white-space-collapse: break-spaces;
  text-wrap: pretty;
}

.CustomerPreviewActivityItem__details--capitalize {
  text-transform: capitalize;
}
</style>
